import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// The session lives in httpOnly cookies the browser holds; JS never sees a token. Only the public profile is cached
// here (for the X-Session-Id ownership header). The old bearer token is dropped once: everyone signs in again.
const LEGACY_TOKEN_KEY = "duckify_token";
const USER_KEY = "duckify_user";
export const CSRF_COOKIE = "__Host-duckify_csrf";
try {
  localStorage.removeItem(LEGACY_TOKEN_KEY);
} catch {}

export function getUser() {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || "null");
  } catch {
    return null;
  }
}
export function setStoredUser(user) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}
export function clearAuth() {
  localStorage.removeItem(LEGACY_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}
export const readCookie = (name) => {
  const hit = document.cookie.split("; ").find((c) => c.startsWith(`${name}=`));
  return hit ? decodeURIComponent(hit.slice(name.length + 1)) : null;
};
const UNSAFE = new Set(["post", "put", "patch", "delete"]);

export function getSessionId() {
  const user = getUser();
  if (user?.id) return `u:${user.id}`;
  let s = localStorage.getItem("duckify_sid");
  if (!s) {
    s = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now() + Math.random());
    localStorage.setItem("duckify_sid", s);
  }
  return s;
}

const api = axios.create({ baseURL: API, withCredentials: true });
api.interceptors.request.use((cfg) => {
  cfg.headers["X-Session-Id"] = getSessionId();
  if (UNSAFE.has((cfg.method || "get").toLowerCase())) {
    const csrf = readCookie(CSRF_COOKIE); // double-submit: the server compares it with its cookie
    if (csrf) cfg.headers["X-CSRF-Token"] = csrf;
  }
  return cfg;
});
// One refresh at a time: every 401 that arrives while it runs waits for the same answer.
let refreshing = null;
export const refreshSession = () => {
  refreshing = refreshing || api.post("/auth/refresh").finally(() => { refreshing = null; });
  return refreshing;
};
const retry = async (error) => {
  const cfg = error?.config;
  const status = error?.response?.status;
  if (!cfg || cfg._retried) return null;
  if (status === 403 && error.response.data?.code === "csrf") {
    await api.get("/auth/csrf");
    return api({ ...cfg, _retried: true });
  }
  if (status === 401 && getUser() && !(cfg.url || "").startsWith("/auth/")) {
    try {
      await refreshSession();
    } catch {
      clearAuth();
      window.dispatchEvent(new Event("duckify-signed-out"));
      return null;
    }
    return api({ ...cfg, _retried: true });
  }
  return null;
};
// A spend-gate refusal comes back as HTTP 202 {detail, queued: true, gate} from ANY paid endpoint (create, render
// redo, regenerate, variations, styles, branch, approve/sculpt, views redo/approve, repaint, story, spec). It is NOT
// a duck, so it must never reach a caller that reads `data.id`: it is turned into a rejection carrying `gate`, and
// every caller's existing error path shows the queue message.
export class QueuedByGate extends Error {
  constructor(gate) {
    super(gate?.why ? `${gate.why} You're in the queue.` : "You're in the queue — generation is paused right now.");
    this.name = "QueuedByGate";
    this.queued = true;
    this.gate = gate || null;
  }
}

api.interceptors.response.use(
  (r) => {
    if (r?.status === 202 && r.data?.queued === true) return Promise.reject(new QueuedByGate(r.data.gate));
    // Paid work happens behind POSTs on /creations: let the allowance meters re-read themselves afterwards.
    if ((r?.config?.method || "").toLowerCase() === "post" && (r.config?.url || "").startsWith("/creations")) window.dispatchEvent(new Event("duckify-spend-changed"));
    return r;
  },
  async (error) => {
    const again = await retry(error);
    if (again) return again;
    if (error?.response?.status === 401 && (error.config?.url || "").startsWith("/library")) window.dispatchEvent(new Event("duckify-library-locked"));
    // A 402 from the per-user spend cap: the meters re-read themselves so "used $x of $y" is current.
    if (error?.response?.status === 402 && error.response.data?.code === "spend_cap") window.dispatchEvent(new Event("duckify-spend-changed"));
    return Promise.reject(error);
  },
);

// Library admin access lives in an httpOnly cookie the browser holds — JS never sees the passcode or a token.
// Error text for a failed mutation: a spend-gate 202 says "you're in the queue", anything else keeps its own wording.
export const errorText = (e, fallback) => {
  if (e?.queued) return e.message;
  const d = e?.response?.data?.detail;
  return (Array.isArray(d) ? d[0]?.msg : d) || fallback;
};

export const unlockLibrary = (passcode) => api.post("/library/unlock", { passcode });
export const lockLibrary = () => api.post("/library/lock");

const tz = () => -new Date().getTimezoneOffset(); // minutes east of UTC, so download filenames carry local time

// Session-scoped assets are fetched with the X-Session-Id header (never in the URL, where it would land in logs/referrers)
export const modelHeaders = () => ({ "X-Session-Id": getSessionId() }); // the session cookie rides along (same origin)
export const modelUrl = (id, rev) => `${API}/creations/${id}/model.glb${rev ? `?v=${encodeURIComponent(rev)}` : ""}`;

// --- Async owner repaint -------------------------------------------------------------------------------------------
// `repaint_status` is persisted on the creation server-side ("running" | "failed" | null), so the "Repainting…"
// overlay survives a page reload. The browser kicks off the repaint then polls GET /creations/{id}.
export const REPAINT_STALE_MS = 12 * 60 * 1000;
const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));
export const startRepaint = (creationId, opts = {}) =>
  api.post(`/creations/${creationId}/repaint`, opts).then((r) => r.data); // 202 → the claimed doc (repaint_status:"running")
export const getCreation = (creationId, signal) =>
  api.get(`/creations/${creationId}`, { signal }).then((r) => r.data);

// Start a repaint (treating a 409 "already running" as success) and resolve with the finished creation doc.
export const repaintAndWait = async (creationId, opts = {}, { signal } = {}) => {
  try {
    await startRepaint(creationId, opts);
  } catch (e) {
    if (e?.response?.status !== 409) throw e; // 409 = one is already running; just poll it to completion
  }
  const deadline = Date.now() + REPAINT_STALE_MS + 60000;
  let delay = 2500;
  for (;;) {
    await _sleep(delay);
    const doc = await getCreation(creationId, signal);
    if (doc.repaint_status === "failed") throw new Error(doc.repaint_error?.reason || "Repaint failed — please try again.");
    if (doc.repaint_status !== "running") return doc;
    if (Date.now() > deadline) throw new Error("Repaint timed out — please try again.");
    delay = Math.min(delay + 500, 6000);
  }
};

export const downloadExport = async (creation, fmt = "3mf", height = 70, colors = 8, onProgress, signal, qr = null) => {
  onProgress?.({ phase: "preparing", percent: 0 });
  const { data } = await api.get(`/creations/${creation.id}/export`, {
    params: { fmt, height, colors, tz: tz(), ...(qr === null || qr === undefined ? {} : { qr: qr ? 1 : 0 }) },
    responseType: "blob",
    signal,
    onDownloadProgress: (e) => {
      // First byte means the server finished preparing and the transfer has begun.
      const percent = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
      onProgress?.({ phase: "downloading", percent });
    },
  });
  onProgress?.({ phase: "downloading", percent: 100 });
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = exportFilename(creation, fmt);
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// Background export jobs: POST to start (or learn it's already cached), poll status, DELETE to cancel.
export const startExportJob = (creation, fmt = "3mf", height = 70, colors = 8, signal, force = false, qr = null) =>
  api.post(`/creations/${creation.id}/export/jobs`, null, {
    params: { fmt, height, colors, tz: tz(), ...(force ? { force: true } : {}), ...(qr === null || qr === undefined ? {} : { qr: qr ? 1 : 0 }) },
    signal,
  }).then((r) => r.data);
export const getExportJob = (creation, jobId, signal) =>
  api.get(`/creations/${creation.id}/export/jobs/${jobId}`, { signal }).then((r) => r.data);
export const cancelExportJob = (creation, jobId) =>
  api.delete(`/creations/${creation.id}/export/jobs/${jobId}`).catch(() => {});
export const shareModelUrl = (id) => `${API}/share/${id}/model.glb`;
export const shareExportUrl = (id, fmt = "3mf", height = 70, colors = 8, qr = null) =>
  `${API}/share/${id}/export?fmt=${fmt}&height=${height}&colors=${colors}&tz=${tz()}${qr === null || qr === undefined ? "" : `&qr=${qr ? 1 : 0}`}`;
export const shareZipUrl = (id) => `${API}/share/${id}/export.zip?tz=${tz()}`;
// Backend preview page carries OG tags (Claude duck name + render) so links unfurl nicely in chats, then redirects to /s/:id
export const shareLink = (id) => `${BACKEND_URL}/api/s/${id}`;
export const sharePageUrl = (id) => `${window.location.origin}/s/${id}`;
export const passportUrl = (id) => `${window.location.origin}/passport/${id}`;
export const labelsUrl = (id) => `${window.location.origin}/labels/${id}`;
// Matte three-quarter render of the printed sculpt — the hero image for previews, passports and labels
export const heroUrl = (id) => `${API}/share/${id}/hero.jpg`;
export const shareBeforeAfterUrl = (id, fmt = "portrait") => `${API}/share/${id}/before-after.png?fmt=${fmt}`;
// Small stored front view of the print — for thumbnails, where the big hero would be wasteful
export const printUrl = (id) => `${API}/share/${id}/turntable.jpg`;
// 128px cached gallery tile, immutable per (version, paint) key — never the full turntable/hero for a 64px <img>.
export const thumbUrl = (id, key) => `${API}/share/${id}/thumb.jpg?v=${encodeURIComponent(key || "1")}`;

// Signed owner tile from a list response ({"256": link, "64": link, ready}); no image data ever rides in a list.
export const tileUrl = (tile, size = "256") => (tile?.[size] ? `${API}${tile[size]}` : BLANK_IMG);

// A duck's FRONT image for display: the AI render if it exists, else the uploaded photo. Uploaded-render ducks store
// the maker's own image in `render` (with a smaller copy in `photo`), so they always resolve to that image here.
export const frontImage = (c) => (c?.render || c?.photo) || null;

// 1x1 transparent GIF — swap a broken <img> to this on error so no broken-image glyph shows over the neutral tile.
export const BLANK_IMG = "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";
export const blankOnError = (e) => {
  if (e?.currentTarget) {
    e.currentTarget.onerror = null;
    e.currentTarget.src = BLANK_IMG;
  }
};
const GALLERY_CACHE_PREFIX = "duckify_gallery_";
export const galleryCacheKey = (uid) => `${GALLERY_CACHE_PREFIX}${uid || "anon"}`;
export const clearGalleryCache = () => {
  Object.keys(localStorage).filter((k) => k.startsWith(GALLERY_CACHE_PREFIX)).forEach((k) => localStorage.removeItem(k));
};
// Update one duck inside every cached first page in place (a rename) instead of dropping the cache.
export const patchGalleryCache = (id, fields) => {
  Object.keys(localStorage).filter((k) => k.startsWith(GALLERY_CACHE_PREFIX)).forEach((k) => {
    try {
      const page = JSON.parse(localStorage.getItem(k));
      if (!page?.items?.some((c) => c.id === id)) return;
      localStorage.setItem(k, JSON.stringify({ ...page, items: page.items.map((c) => (c.id === id ? { ...c, ...fields } : c)) }));
    } catch {}
  });
};
export const qrJsonUrl = (id) => `${API}/share/${id}/qr.json`;
export const embedUrl = (id) => `${window.location.origin}/embed/${id}`;
export const embedSnippet = (id) =>
  `<iframe src="${embedUrl(id)}" width="480" height="480" style="border:0;border-radius:24px" loading="lazy" title="Duckify3D viewer"></iframe>`;

export const PRINT_HEIGHTS = [70, 85, 100];
export const DEFAULT_HEIGHT = 70;

// Duckify_Name_MMDDYYYY_HH-MM-SS_vN.ext — same schema the backend puts in Content-Disposition (local time).
export const safeDuckName = (c) => ((c?.name || c?.story?.name || "Duck").trim().replace(/[^A-Za-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40) || "Duck");

export const exportFilename = (c, ext) => {
  const name = safeDuckName(c);
  const d = new Date(c.built_at || c.created_at || Date.now());
  const p = (n) => String(n).padStart(2, "0");
  const stamp = `${p(d.getMonth() + 1)}${p(d.getDate())}${d.getFullYear()}_${p(d.getHours())}-${p(d.getMinutes())}-${p(d.getSeconds())}`;
  return `Duckify_${name}_${stamp}_v${c.version || 1}.${ext}`;
};

// AI render downloads (owner only — fetched with the session headers, never a bare <img src>).
export const RENDER_VIEWS = ["front", "back", "left", "right", "closeup"];
export const renderFilename = (c, view, ext = "png") => `Duckify_${safeDuckName(c)}_${view}.${ext}`;
export const renderImagePath = (c, view, thumb = false) => `/creations/${c.id}/render/${view}.png?v=${c.version || 1}${thumb ? "&thumb=1" : ""}`;
export const fetchRenderBlob = (c, view, thumb = false) => api.get(renderImagePath(c, view, thumb), { responseType: "blob" }).then((r) => r.data);
export const downloadRendersZip = async (c, onProgress) => {
  const { data } = await api.get(`/creations/${c.id}/renders.zip`, {
    responseType: "blob",
    onDownloadProgress: (e) => onProgress?.(e.total ? Math.round((e.loaded / e.total) * 100) : null),
  });
  saveBlob(data, `Duckify_${safeDuckName(c)}_renders.zip`);
};
export const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const duckTitle = (c) => c?.name || c?.story?.name || "";

export const MAX_NAME = 60;
export const MAX_STORY = 600;
// Owner rename / hand-edited story: {name?, story_name?, story_description?} → the updated public creation.
export const editCreation = (id, body) => api.patch(`/creations/${id}`, body);

export default api;
